/* ============================================================
   3D layer — one GLB, loaded once, driven by scroll.
   ============================================================ */
import * as THREE from 'three';
import { GLTFLoader }      from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader }     from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const O      = window.__onyx;
const loader = document.getElementById('loader');
const pctEl  = document.getElementById('pct');
const barEl  = document.getElementById('loadbar');

/* Bail cleanly rather than showing an empty black page. */
function webglOK(){
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch(e){ return false; }
}

const disabled = window.__ONYX && window.__ONYX.enable3d === false;
if(disabled || !webglOK() || O.REDUCED){
  O.fallback(disabled ? 'disabled in theme settings'
            : O.REDUCED ? 'prefers-reduced-motion' : 'no WebGL');
} else {
  boot();
}

function boot(){
  const canvas   = document.getElementById('gl');
  const renderer = new THREE.WebGLRenderer({canvas, antialias:true, alpha:true, powerPreference:'high-performance'});
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(innerWidth, innerHeight, false);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene  = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(34, innerWidth/innerHeight, 0.1, 100);
  camera.position.set(0, 0, 6);

  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  const key  = new THREE.DirectionalLight(0xffffff, 2.4); key.position.set(2.5, 3, 4);  scene.add(key);
  const rimL = new THREE.DirectionalLight(0x4C7DFF, 5.0); rimL.position.set(-4, 1, -2); scene.add(rimL);
  const rimR = new THREE.DirectionalLight(0x4C7DFF, 3.2); rimR.position.set(4, -1, -3); scene.add(rimR);
  scene.add(new THREE.AmbientLight(0xffffff, 0.35));

  /* group carries the scroll pose; each unit sits inside it and carries only
     its own transition offsets, so the two never fight over the same transform */
  const group = new THREE.Group();
  scene.add(group);

  /* ---------- which model each screen uses ----------
     Two models so far, so they alternate. When the remaining models arrive,
     give each screen its own entry and the alternation stops being a stand-in. */
  const HOSTED = (window.__ONYX && window.__ONYX.models) || null;   // Shopify CDN URLs
  /* `panel` pins the display rect in the model's own centred+scaled space.
     Auto-detection picks the largest camera-facing plane, which is right for the
     wall unit but wrong for the totem — there the largest plane is the whole
     front slab and the screen is a shallower recess behind it. Measured values
     win; anything without them falls back to detection. */
  const MODELS = [
    {url: (HOSTED && HOSTED[0]) || 'assets/signage-opt.glb',
     label: 'wall/desk screen'},
    {url: (HOSTED && HOSTED[1]) || 'assets/longfield-totem.glb',
     label: 'freestanding totem',
     panel: {x: 0.0016, y: 0.3837, w: 1.0284, h: 1.5276, z: 0.0625, out: 0.002}}
  ];
  const SLIDE_MODEL = [0, 1, 0, 1];
  const units = [];
  let activeUnit = 0;

  let pct = 0;
  function setPct(v){
    pct = Math.max(pct, Math.min(100, Math.round(v)));
    pctEl.textContent = pct + '%';
    barEl.style.width = pct + '%';
  }

  const draco = new DRACOLoader().setDecoderPath('https://unpkg.com/three@0.160.0/examples/jsm/libs/draco/');
  const gltfLoader = new GLTFLoader().setDRACOLoader(draco);

  /* ---------- find the display surface from the geometry ----------
     The bounding box includes the stand and base, and the frontmost surface is
     the plinth, not the glass. So: take every camera-facing vertex, bin by
     depth, and keep the plane with the largest area. */
  function findScreenFace(root){
    root.updateWorldMatrix(true, true);
    const v = new THREE.Vector3(), n = new THREE.Vector3();
    const facing = [];
    root.traverse((o) => {
      if(!o.isMesh || !o.geometry) return;
      const pos = o.geometry.attributes.position, nor = o.geometry.attributes.normal;
      if(!pos || !nor) return;
      const nmat = new THREE.Matrix3().getNormalMatrix(o.matrixWorld);
      for(let i = 0; i < pos.count; i++){
        n.fromBufferAttribute(nor, i).applyMatrix3(nmat).normalize();
        if(n.z < 0.85) continue;
        v.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld);
        facing.push(v.x, v.y, v.z);
      }
    });
    if(facing.length < 12) return null;
    const BIN = 0.01, bins = new Map();
    for(let i = 0; i < facing.length; i += 3){
      const k = Math.round(facing[i+2] / BIN);
      let b = bins.get(k);
      if(!b){ b = {minX:Infinity,maxX:-Infinity,minY:Infinity,maxY:-Infinity,z:-Infinity,n:0}; bins.set(k, b); }
      b.minX = Math.min(b.minX, facing[i]);   b.maxX = Math.max(b.maxX, facing[i]);
      b.minY = Math.min(b.minY, facing[i+1]); b.maxY = Math.max(b.maxY, facing[i+1]);
      b.z = Math.max(b.z, facing[i+2]); b.n++;
    }
    let best = null, bestArea = 0;
    bins.forEach((b) => {
      const w = b.maxX - b.minX, h = b.maxY - b.minY;
      if(w < 1e-4 || h < 1e-4) return;
      if(Math.max(w,h) / Math.min(w,h) > 6) return;
      const area = w * h;
      if(area > bestArea){ bestArea = area; best = {w, h, x:(b.minX+b.maxX)/2, y:(b.minY+b.maxY)/2, z:b.z, verts:b.n}; }
    });
    return best;
  }

  function shade(hex, amt){
    const c = new THREE.Color(hex); c.multiplyScalar(1 + amt);
    return '#' + c.getHexString();
  }

  /* draw the creative onto one unit's own canvas */
  function drawCreative(u, accent, line1, line2){
    if(!u.cv) return;
    const g = u.cv.getContext('2d'), W = u.cv.width, H = u.cv.height;
    const grad = g.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, accent); grad.addColorStop(1, shade(accent, -0.45));
    g.fillStyle = grad; g.fillRect(0, 0, W, H);
    g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle';
    const big = Math.round(W * 0.133);
    g.font = '700 ' + big + 'px "Libre Franklin", Arial, sans-serif';
    g.fillText(String(line1).toUpperCase(), W/2, H/2 - big*0.6);
    g.fillText(String(line2).toUpperCase(), W/2, H/2 + big*0.6);
    g.globalAlpha = .75;
    g.font = '500 ' + Math.round(W * 0.042) + 'px "Geist Mono", monospace';
    g.fillText('LONGFIELD', W/2, H - big*0.95);
    g.globalAlpha = 1;
    if(u.tex) u.tex.needsUpdate = true;
  }

  function addPanel(u, size, s){
    u.cv = document.createElement('canvas');
    u.cv.width = 720; u.cv.height = 1000;
    drawCreative(u, '#4C7DFF', 'Family', 'days out');
    u.tex = new THREE.CanvasTexture(u.cv);
    u.tex.colorSpace = THREE.SRGBColorSpace;

    let fit;
    if(u.spec && u.spec.panel){
      const q = u.spec.panel;
      fit = {w: q.w * 0.985, h: q.h * 0.985, x: q.x, y: q.y, z: q.z + (q.out || 0.004)};
      u.face = 'measured';
    } else {
      const face = findScreenFace(u.model);
      u.face = face;
      fit = face
        ? {w: face.w * 0.965, h: face.h * 0.965, x: face.x, y: face.y, z: face.z + 0.004}
        : {w: size.x * s * 0.88, h: size.y * s * 0.60, x: 0, y: size.y * s * 0.19, z: size.z * s * 0.503};
    }

    /* match the canvas to the panel's real aspect or the type stretches */
    u.cv.width  = 720;
    u.cv.height = Math.max(200, Math.round(720 * (fit.h / fit.w)));
    drawCreative(u, '#4C7DFF', 'Family', 'days out');

    const mat = new THREE.MeshBasicMaterial({map: u.tex, toneMapped: false, transparent: true});
    u.panel = new THREE.Mesh(new THREE.PlaneGeometry(fit.w, fit.h), mat);
    u.panel.position.set(fit.x, fit.y, fit.z);
    u.root.add(u.panel);
    u.fit = fit;

    u.spill = new THREE.PointLight(0x4C7DFF, 2.4, 4, 2);
    u.spill.position.set(fit.x, fit.y, fit.z + 0.25);
    u.root.add(u.spill);
  }

  /* Materials are transparent from the start so the crossfade never recompiles a
     shader mid-transition. depthWrite goes off while a unit is part-faded —
     with it on, two overlapping models z-fight and the swap looks like it's
     tearing, which is what made the old transition read as janky. */
  function prepFade(u){
    u.mats = [];
    u.root.traverse((o) => {
      if(!o.isMesh || !o.material) return;
      const list = Array.isArray(o.material) ? o.material : [o.material];
      list.forEach((m) => { m.transparent = true; u.mats.push(m); });
    });
  }
  function setUnitOpacity(u, v){
    const solid = v > 0.995;
    for(let i = 0; i < u.mats.length; i++){
      u.mats[i].opacity = v;
      u.mats[i].depthWrite = solid;
    }
    if(u.spill) u.spill.intensity = 2.4 * v;
  }
  /* ease-in-out — constant-speed lerps start and stop abruptly */
  function ease(t){ return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2; }

  function loadUnit(spec, i){
    return new Promise((resolve, reject) => {
      gltfLoader.load(spec.url, (gltf) => {
        const u = {root: new THREE.Group(), model: gltf.scene, label: spec.label, spec: spec, vis: i === 0 ? 1 : 0};
        const box  = new THREE.Box3().setFromObject(u.model);
        const size = box.getSize(new THREE.Vector3());
        const mid  = box.getCenter(new THREE.Vector3());
        const s = 2.3 / (Math.max(size.x, size.y, size.z) || 1);
        u.model.position.sub(mid);
        u.model.scale.setScalar(s);
        u.root.add(u.model);
        group.add(u.root);
        addPanel(u, size, s);
        prepFade(u);
        setUnitOpacity(u, u.vis);
        u.root.visible = u.vis > 0.001;
        u.size = size.toArray().map(n => +n.toFixed(3));
        units[i] = u;
        resolve(u);
      }, (e) => {
        if(e.total) setPct(((i + e.loaded / e.total) / MODELS.length) * 92);
        else setPct(pct + 1);
      }, reject);
    });
  }

  Promise.all(MODELS.map(loadUnit))
    .then(() => {
      O.modelInfo = units.map(u => ({label: u.label, size: u.size}));
      setPct(100);
      renderer.render(scene, camera);          // compile before the reveal
      let revealed = false;
      const reveal = () => {
        if(revealed) return;
        revealed = true;
        makeThumb();
        loader.classList.add('done');
        start();
      };
      requestAnimationFrame(reveal);
      setTimeout(reveal, 400);
    })
    .catch((err) => { O.fallback('model failed to load: ' + err); });

  /* ---------- accent + creative apply to every unit ---------- */
  O.setLightColour = function(hex){
    const c = new THREE.Color(hex);
    rimL.color.copy(c); rimR.color.copy(c);
    const s = O.currentCreative || ['Family', 'days out'];
    units.forEach((u) => { if(u){ if(u.spill) u.spill.color.set(hex); drawCreative(u, hex, s[0], s[1]); } });
  };
  O.setCreative = function(l1, l2, hex){
    O.currentCreative = [l1, l2];
    units.forEach((u) => { if(u) drawCreative(u, hex || '#4C7DFF', l1, l2); });
  };
  /* which model this screen shows — called from the scroll driver */
  const SWAP_MS = 620;
  let swapFrom = 0, swapT = 1;      // swapT 0..1 across the crossfade
  O.setUnit = function(slideIndex){
    const want = SLIDE_MODEL[slideIndex % SLIDE_MODEL.length] || 0;
    if(want === activeUnit) return;
    /* if a swap is already mid-flight, start the new one from where it is */
    swapFrom = activeUnit;
    activeUnit = want;
    swapT = 0;
  };

  /* ---------- one offscreen render per screen, for the range grid ---------- */
  function makeThumb(){
    try{
      const N  = 512;
      const c2 = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
      c2.position.set(1.4, 0.4, 5.2); c2.lookAt(0, 0, 0);
      const rt  = new THREE.WebGLRenderTarget(N, N);
      const buf = new Uint8Array(N * N * 4);
      const cv  = document.createElement('canvas'); cv.width = cv.height = N;
      const ctx = cv.getContext('2d');

      const rot = group.rotation.y, px = group.position.x, py = group.position.y;
      group.rotation.y = -0.5; group.position.set(0, 0, 0);
      const wasVis = units.map(u => ({v: u.root.visible, s: u.root.scale.x, y: u.root.position.y}));
      units.forEach((u) => { u.root.scale.setScalar(1); u.root.position.y = 0; });

      const slides = [].slice.call(document.querySelectorAll('.slide'));
      const srcs = slides.map((s, i) => {
        const acc = s.getAttribute('data-acc') || '#4C7DFF';
        const cr  = (s.getAttribute('data-creative') || 'Family|days out').split('|');
        const ui  = SLIDE_MODEL[i % SLIDE_MODEL.length] || 0;
        units.forEach((u, n) => { u.root.visible = (n === ui); setUnitOpacity(u, n === ui ? 1 : 0); });
        drawCreative(units[ui], acc, cr[0], cr[1]);
        rimL.color.set(acc); rimR.color.set(acc);
        if(units[ui].spill) units[ui].spill.color.set(acc);

        renderer.setRenderTarget(rt);
        renderer.render(scene, c2);
        renderer.readRenderTargetPixels(rt, 0, 0, N, N, buf);
        renderer.setRenderTarget(null);

        const id = ctx.createImageData(N, N);
        for(let y = 0; y < N; y++){
          id.data.set(buf.subarray((N-1-y)*N*4, (N-1-y)*N*4 + N*4), y*N*4);
        }
        ctx.putImageData(id, 0, 0);
        return cv.toDataURL('image/png');
      });

      group.rotation.y = rot; group.position.set(px, py, 0);
      units.forEach((u, n) => { u.root.visible = wasVis[n].v; u.root.scale.setScalar(wasVis[n].s); u.root.position.y = wasVis[n].y; });
      O.paintThumbs(srcs);
      rt.dispose();
      renderer.setSize(innerWidth, innerHeight, false);
    } catch(e){ console.info('thumbnail capture skipped:', e); }
  }

  /* ---------- scroll → pose ---------- */
  const target  = {rx:0, ry:-0.5, z:6, y:0, x:0};
  const current = {rx:0, ry:-0.5, z:6, y:0, x:0};
  const MODEL_H = 2.3;
  const HALF_FOV = Math.tan(34 * Math.PI / 360);

  function fitBelowNav(){
    const nav = document.querySelector('.nav');
    /* the product card sits under the model on desktop, so it — not the
       progress rail — is what the model has to stay clear of */
    const stop = document.querySelector('.range-copy') || document.querySelector('.rangebar');
    if(!nav || !stop) return null;
    const bandTop = nav.getBoundingClientRect().bottom + 56;
    const bandBot = stop.getBoundingClientRect().top - 22;
    const bandPx  = bandBot - bandTop;
    if(bandPx < 160) return null;
    const halfH = (MODEL_H / 2) * (innerHeight / (bandPx * 0.78));
    return {
      z: Math.min(Math.max(halfH / HALF_FOV, 5.2), 20),
      y: ((innerHeight / 2) - (bandTop + bandPx / 2)) / (innerHeight / 2) * halfH
    };
  }
  function fitToBand(){
    const top  = document.querySelector('.range-top');
    /* The product card is hidden on a phone (it was sitting on the model), so
       the floor is whichever of the two is actually laid out: the card if it is
       visible, otherwise the progress rail. offsetParent is null under
       display:none, which is exactly the case we need to catch. */
    const card = document.querySelector('.range-copy');
    const copy = (card && card.offsetParent !== null)
      ? (document.querySelector('.slide.on') || card)
      : document.querySelector('.rangebar');
    if(!top || !copy) return null;
    const bandTop = top.getBoundingClientRect().bottom + 28;   // clear of the CTAs
    const bandBot = copy.getBoundingClientRect().top - 20;
    const bandPx  = bandBot - bandTop;
    if(bandPx < 120) return null;
    const halfH = (MODEL_H / 2) * (innerHeight / (bandPx * 0.88));
    return {
      z: Math.min(Math.max(halfH / HALF_FOV, 6), 22),
      y: ((innerHeight / 2) - (bandTop + bandPx / 2)) / (innerHeight / 2) * halfH
    };
  }

  O.onScroll = function(p){
    target.ry = -0.7 + p.range * 2.4;
    target.rx = 0.04;
    if(innerWidth > 900){
      const fit = fitBelowNav();
      target.x = 1.05;
      target.y = fit ? fit.y : -0.16;
      target.z = fit ? fit.z : 6.6;
    } else {
      const fit = fitToBand();
      target.x = 0;
      target.y = fit ? fit.y : 0.60;
      target.z = fit ? fit.z : 10.0;
    }
  };
  O.fitToBand = fitToBand;

  let pointerX = 0, pointerY = 0;
  addEventListener('pointermove', (e) => {
    pointerX = (e.clientX / innerWidth  - 0.5);
    pointerY = (e.clientY / innerHeight - 0.5);
  }, {passive:true});

  function start(){
    O.refreshSlide();
    renderer.setAnimationLoop(tick);
  }

  function tick(){
    const k = 0.075;
    current.rx += (target.rx + pointerY * 0.12 - current.rx) * k;
    current.ry += (target.ry + pointerX * 0.22 - current.ry) * k;
    current.x  += (target.x - current.x) * k;
    current.y  += (target.y - current.y) * k;
    current.z  += (target.z - current.z) * k;
    group.rotation.x = current.rx;
    group.rotation.y = current.ry;
    group.position.x = current.x;
    group.position.y = current.y;
    camera.position.z = current.z;

    /* Crossfade on a clock, not a lerp: the same 620ms whatever the frame rate,
       eased at both ends. The outgoing unit leads slightly so the two are never
       both fully opaque, which is what produced the ghosted double image. */
    if(swapT < 1){
      swapT = Math.min(1, swapT + (1000 / 60) / SWAP_MS);
    }
    const e = ease(swapT);
    for(let i = 0; i < units.length; i++){
      const u = units[i];
      if(!u) continue;
      const incoming = (i === activeUnit);
      const outgoing = (i === swapFrom && swapFrom !== activeUnit);
      if(incoming)      u.vis = e;
      else if(outgoing) u.vis = 1 - Math.min(1, e * 1.35);   // leaves a touch early
      else              u.vis = 0;
      u.root.visible = u.vis > 0.004;
      if(!u.root.visible) continue;
      setUnitOpacity(u, u.vis);
      /* scale only — no rotation swing; the group already carries the turn */
      u.root.scale.setScalar(0.94 + 0.06 * u.vis);
      u.root.position.y = (1 - u.vis) * -0.10;
    }
    renderer.render(scene, camera);
  }

  addEventListener('resize', () => {
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight, false);
  });

  /* test hooks */
  O.gl = {
    get: () => ({
      loaded: units.length === MODELS.length && units.every(Boolean),
      activeUnit: activeUnit,
      vis: units.map(u => u ? +u.vis.toFixed(3) : null),
      target: {...target}, current: {...current},
      camZ: +camera.position.z.toFixed(3),
      rim: '#' + rimL.color.getHexString()
    }),
    poseAt: (p) => { O.onScroll(p); return {...target}; },
    step: (n) => { for(let i = 0; i < (n || 60); i++) tick(); return {...current}; },
    /* the visible unit's extent in CSS pixels — what the copy actually has to
       stay clear of, measured rather than assumed */
    bounds: () => {
      const u = units[activeUnit]; if(!u) return null;
      const box = new THREE.Box3().setFromObject(u.root);
      let top = Infinity, bot = -Infinity, left = Infinity, right = -Infinity;
      for(let i = 0; i < 8; i++){
        const v = new THREE.Vector3(
          i & 1 ? box.max.x : box.min.x,
          i & 2 ? box.max.y : box.min.y,
          i & 4 ? box.max.z : box.min.z).project(camera);
        const x = (v.x * 0.5 + 0.5) * innerWidth, y = (-v.y * 0.5 + 0.5) * innerHeight;
        top = Math.min(top, y); bot = Math.max(bot, y);
        left = Math.min(left, x); right = Math.max(right, x);
      }
      return {top: Math.round(top), bottom: Math.round(bot),
              left: Math.round(left), right: Math.round(right)};
    }
  };
}
